import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, FlaskConical, Leaf, ShieldCheck, Truck, ArrowDown } from "lucide-react";
import api, { FALLBACK_IMG } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import FreeGuideSignup from "@/components/FreeGuideSignup";
import { FAQS as ALL_FAQS } from "@/data/faqs";

const HOME_FAQS = ALL_FAQS.slice(0, 6);


const HERO = "https://images.unsplash.com/photo-1760982419970-938c25f8cb8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const CRAFT_A = "https://images.unsplash.com/photo-1760273297730-bde08f332773?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100";
const CRAFT_B = "https://images.unsplash.com/photo-1762026430675-1262fb4e364a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100";
const STORY = "https://images.unsplash.com/photo-1505820013142-f86a3439c5b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const lineParent = { hidden: {}, visible: { transition: { staggerChildren: 0.11, delayChildren: 0.25 } } };
const lineChild = { hidden: { y: "115%" }, visible: { y: 0, transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] } } };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } };


function Spores() {
  const dots = Array.from({ length: 20 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
      {dots.map((_, i) => {
        const size = 2 + Math.random() * 4;
        return (
          <span key={i} className="spore-dot" style={{
            width: size, height: size, left: `${Math.random() * 100}%`, bottom: `-${Math.random() * 20}%`,
            animationDuration: `${8 + Math.random() * 10}s`, animationDelay: `${Math.random() * 8}s`,
          }} />
        );
      })}
    </div>
  );
}

const FEATURES = [
  { icon: FlaskConical, title: "Lab Prepared", desc: "Cultured in a HEPA-filtered lab, QC-checked for purity." },
  { icon: ShieldCheck, title: "Contamination-Free", desc: "Every syringe sterility-tested before it ships." },
  { icon: Leaf, title: "Gourmet & Medicinal", desc: "Lion's Mane, Reishi, Cordyceps, Oyster and more." },
  { icon: Truck, title: "Ships Nationwide", desc: "From North Carolina to growers across the U.S." },
];

const CHAPTERS = [
  {
    n: "01", tag: "The Source", title: "It begins in a sterile lab.",
    body: "Every culture starts on agar under a HEPA-filtered flow hood. We isolate the cleanest, most vigorous genetics, then propagate them into living liquid mycelium, so what lands in your jar is pure, dense, and ready to run.",
    img: CRAFT_A,
  },
  {
    n: "02", tag: "The Standard", title: "Nothing ships until it is clean.",
    body: "Contamination is the number one reason grows fail, so we obsess over it. Each batch is QC-checked and sterility-tested before it leaves the lab. If a culture is not something we would inoculate ourselves, it never reaches you.",
    img: CRAFT_B,
  },
  {
    n: "03", tag: "The Grow", title: "We hand you the whole playbook.",
    body: "Cultures, grain spawn, all-in-one bags, bucket kits, and in-depth guides written from real bench experience. Whether it is your first jar or your fiftieth flush, we give you the genetics and the knowledge to get there.",
    img: STORY,
  },
];

const STRAINS = ["Reishi", "Lion's Mane", "Blue Oyster", "Shiitake", "Turkey Tail", "Cordyceps", "Maitake", "King Oyster"];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [journal, setJournal] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/products").then((r) => {
      const picked = r.data.filter((p) => p.featured).slice(0, 4);
      // fall back to the first few products so the section is never empty
      setFeatured(picked.length ? picked : r.data.slice(0, 4));
    });
    api.get("/posts?limit=3").then((r) => setJournal((r.data.posts || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <Seo path="/" description="Clean, lab-prepared liquid mushroom cultures, all-in-one grow bags, 5-gallon bucket kits, and in-depth cultivation guides. Sterile, healthy, ready to grow. Shipped from North Carolina.">
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: HOME_FAQS.map((f) => ({ "@type": "Question", name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a } })),
        })}</script>
      </Seo>

      {/* ---------------- KINETIC HERO ---------------- */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden border-b border-border">
        <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale: bgScale }}>
          <img src={HERO} alt="Macro mushroom in low light" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />
        </motion.div>
        <Spores />

        {/* Brand badge emblem — the star of the hero */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:flex items-center justify-center absolute right-6 xl:right-16 top-28 bottom-24 z-[2] pointer-events-none"
          data-testid="hero-badge">
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-primary/25 blur-[70px] scale-90" />
            <motion.img
              src="/nc-logo.png" alt="NC Mycology badge"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-[260px] xl:w-[330px] max-h-full h-auto object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.55)]" />
          </div>
        </motion.div>

        <motion.div style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-[3] w-full max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono-label text-xs text-primary mb-7">
            North Carolina &middot; Est. Lab Culture
          </motion.p>

          <motion.h1 variants={lineParent} initial="hidden" animate="visible"
            className="font-display font-light text-[3.4rem] sm:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tight max-w-5xl">
            <span className="reveal-mask"><motion.span variants={lineChild} className="block">Grow bolder.</motion.span></span>
            <span className="reveal-mask"><motion.span variants={lineChild} className="block text-primary italic">Start with the source.</motion.span></span>
          </motion.h1>

          <motion.p variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.9 }}
            className="mt-8 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Clean, lab-prepared liquid mycelium cultures, gourmet grow kits, and in-depth
            cultivation guides. Everything you need for a contamination-free grow.
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-4">
            <Link to="/shop" data-testid="hero-shop-btn"
              className="group inline-flex items-center gap-2 bg-primary hover:brightness-110 text-primary-foreground font-mono-label text-xs px-8 py-4 rounded-full transition-[filter,transform] active:scale-95">
              Shop All <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/shop?category=Ebooks+%26+Guides" data-testid="hero-guides-btn"
              className="inline-flex items-center gap-2 border border-border hover:border-primary hover:text-primary font-mono-label text-xs px-8 py-4 rounded-full transition-colors">
              Browse Guides
            </Link>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-5 sm:left-8 z-[3] flex flex-col items-center gap-2">
          <span className="font-mono-label text-[10px] text-muted-foreground">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
            <ArrowDown className="w-4 h-4 text-primary" />
          </motion.span>
        </motion.div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <section className="border-b border-border bg-card/40 py-6">
        <Marquee items={STRAINS} />
      </section>

      {/* ---------------- FREE GUIDE / LEAD MAGNET (up top for visibility) ---------------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 lg:pt-20" data-testid="home-free-guide">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
          className="relative overflow-hidden border border-primary/30 bg-primary/[0.04] rounded-sm p-8 lg:p-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <div className="flex-1">
            <span className="inline-block font-mono-label text-[10px] text-primary border border-primary/40 rounded-full px-3 py-1 mb-4">
              Free · $100 Value
            </span>
            <h2 className="font-display text-3xl lg:text-4xl font-light leading-tight mb-3">
              Get "Build Your Own Mycology Lab" — free
            </h2>
            <p className="text-muted-foreground leading-relaxed max-w-xl">
              Our complete blueprint for a contamination-free home lab on any budget. Join the list and
              we'll email your download — plus a code for 15% off your first order.
            </p>
          </div>
          <div className="md:w-[380px] shrink-0">
            <FreeGuideSignup variant="full" />
          </div>
        </motion.div>
      </section>

      {/* ---------------- FEATURES ---------------- */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08 }}
              className="group p-8 border-r border-b lg:border-b-0 border-border last:border-r-0 hover:bg-card/60 transition-colors">
              <f.icon className="w-6 h-6 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-display text-xl mb-1">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- MANIFESTO CHAPTERS ---------------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-36">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          className="mb-16 md:mb-24 max-w-3xl">
          <p className="font-mono-label text-xs text-primary mb-4">The NC Mycology Method</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] text-balance">
            Three things we never cut corners on.
          </h2>
        </motion.div>

        <div className="space-y-20 md:space-y-32">
          {CHAPTERS.map((c, i) => (
            <div key={c.n} className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="lg:col-span-6 relative overflow-hidden aspect-[4/3] bg-secondary [direction:ltr]">
                <img src={c.img} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute top-5 left-5 font-mono-label text-[10px] px-2.5 py-1 bg-background/70 glass border border-border">
                  {c.tag}
                </span>
              </motion.div>

              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
                className="lg:col-span-6 [direction:ltr]">
                <span className="font-display text-7xl md:text-8xl font-light text-primary/25 leading-none block mb-4">{c.n}</span>
                <h3 className="font-display text-3xl md:text-4xl font-light mb-5 leading-tight">{c.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-base md:text-lg max-w-lg">{c.body}</p>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- FEATURED ---------------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          className="flex items-end justify-between mb-12">
          <div>
            <p className="font-mono-label text-xs text-primary mb-3">Best Sellers</p>
            <h2 className="font-display text-4xl sm:text-5xl font-light">Fan favourites</h2>
          </div>
          <Link to="/shop" className="font-mono-label text-xs text-muted-foreground hover:text-primary transition-colors hidden sm:inline-flex items-center gap-1 nav-underline">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* ---------------- STORY / CTA ---------------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          className="relative overflow-hidden border border-border bg-card">
          <div className="grid lg:grid-cols-2">
            <div className="relative min-h-[320px] lg:min-h-full overflow-hidden bg-secondary">
              <img src={STORY} alt="Forest mushrooms" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/90 lg:to-card" />
            </div>
            <div className="p-10 lg:p-16 flex flex-col justify-center">
              <p className="font-mono-label text-xs text-primary mb-4">Our Craft</p>
              <h2 className="font-display text-4xl lg:text-5xl font-light leading-tight mb-6">
                Five years of clean culture work.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                NC Mycology specializes in gourmet and medicinal mushroom products. With over five years of
                expertise, we give home growers the essentials for a reliable grow, starting with
                high-quality, contamination-free liquid culture syringes.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Sterile technique, sustainable practices, and a genuine love for fungi. Whether you are
                inoculating your first jar or scaling a small batch, we are your source.
              </p>
              <Link to="/shop" className="mt-8 inline-flex items-center gap-2 font-mono-label text-xs text-primary hover:gap-3 transition-all w-fit">
                Explore the shop <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ---------------- FAQ (GEO / AI Overviews) ---------------- */}
      <section id="faq" className="max-w-5xl mx-auto px-5 sm:px-8 py-24 border-t border-border" data-testid="home-faq">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="mb-14">
          <p className="font-mono-label text-xs text-primary mb-3">Answers</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light">Frequently asked questions</h2>
        </motion.div>
        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
          {HOME_FAQS.map((f, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true, margin: "-40px" }} transition={{ delay: (i % 2) * 0.06 }}
              data-testid={`faq-item-${i}`}>
              <h3 className="font-display text-lg font-normal mb-2 leading-snug">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </motion.div>
          ))}
        </div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3">
          <Link to="/faq" data-testid="faq-viewall-cta"
            className="inline-flex items-center gap-2 font-mono-label text-xs text-primary hover:underline">
            View all FAQs <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link to="/contact" data-testid="faq-contact-cta"
            className="inline-flex items-center gap-2 font-mono-label text-xs text-muted-foreground hover:text-primary transition-colors">
            Still have a question? Contact us
          </Link>
        </motion.div>
      </section>

      {/* ---------------- FROM THE JOURNAL ---------------- */}
      {journal.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28" data-testid="home-journal">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
            className="flex items-end justify-between mb-12">
            <div>
              <p className="font-mono-label text-xs text-primary mb-3">From the Journal</p>
              <h2 className="font-display text-4xl sm:text-5xl font-light">Grow smarter</h2>
            </div>
            <Link to="/blog" className="font-mono-label text-xs text-muted-foreground hover:text-primary transition-colors hidden sm:inline-flex items-center gap-1 nav-underline">
              All guides <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {journal.map((p, i) => (
              <motion.div key={p.slug} variants={fadeUp} initial="hidden" whileInView="visible"
                viewport={{ once: true, margin: "-40px" }} transition={{ delay: (i % 3) * 0.08 }}
                data-testid={`home-journal-card-${p.slug}`}>
                <Link to={`/blog/${p.slug}`} className="group flex flex-col h-full border border-border bg-card/40 rounded-sm overflow-hidden hover:border-primary/60 transition-colors">
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img src={p.featured_image_url || FALLBACK_IMG} alt={p.title}
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute top-3 left-3 font-mono-label text-[10px] px-2 py-1 bg-background/85 backdrop-blur text-primary border border-primary/30">{p.category}</span>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <h3 className="font-display text-xl font-light leading-snug mb-3 group-hover:text-primary transition-colors">{p.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">{p.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 font-mono-label text-[11px] text-primary">
                      Read Guide <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
